import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  QueryList,
  Self,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NgControl } from '@angular/forms';
import moment, { Moment } from 'moment';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';
import { DateFormat } from '@nimbus-library/features/localization/models/enums/language.enum';

interface DatePart {
  type: 'year' | 'month' | 'date';
  maxLength: number;
  maxValue?: number;
}

@Component({
  standalone: false,
  selector: 'nimbus-filter-date-range-input',
  templateUrl: './nimbus-filter-date-range-input.component.html',
  styleUrls: ['./nimbus-filter-date-range-input.component.scss'],
  providers: [{ provide: MatFormFieldControl, useExisting: NimbusFilterDateRangeInputComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusFilterDateRangeInputComponent implements MatFormFieldControl<Moment>, ControlValueAccessor, OnDestroy, OnChanges {
  public static nextId = 0;

  @Input() public dateFormat: DateFormat = DateFormat.English;

  @HostBinding() public id = `nimbus-date-input-${NimbusFilterDateRangeInputComponent.nextId++}`;

  @ViewChildren('datePartInput') public datePartInputs: QueryList<ElementRef<HTMLInputElement>>;

  private internalPlaceholder: string;
  private internalRequired = false;
  private internalDisabled = false;

  public readonly controlType: string = 'date-input';
  public readonly autofilled: boolean = false;
  public readonly stateChanges = new Subject<void>();
  public readonly userAriaDescribedBy: string;
  public focused: boolean;
  public touched: boolean;
  public form: FormGroup;

  public separator: '.' | '/' | '-' = '/';
  public dateParts: DatePart[];

  @Input()
  public get value(): Moment | null {
    const dateFormValue = this.form.value;

    if (dateFormValue.year && dateFormValue.month && dateFormValue.date) {
      return moment({
        year: dateFormValue.year,
        month: dateFormValue.month - 1,
        date: dateFormValue.date,
      });
    }

    return null;
  }
  public set value(date: Moment | null) {
    this.form.setValue({
      year: date ? Math.floor(date.year()).toString().padStart(4, '0') : '',
      month: date ? (date.month() + 1).toString().padStart(2, '0') : '',
      date: date ? date.date().toString().padStart(2, '0') : '',
    });
    this.stateChanges.next();
  }

  @Input()
  public get placeholder(): string {
    return this.internalPlaceholder;
  }
  public set placeholder(val: string) {
    this.internalPlaceholder = val;
    this.stateChanges.next();
  }

  @Input()
  public get required(): boolean {
    return this.internalRequired;
  }
  public set required(req: boolean) {
    this.internalRequired = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  @Input()
  public get disabled(): boolean {
    return this.internalDisabled;
  }
  public set disabled(value: boolean) {
    this.internalDisabled = coerceBooleanProperty(value);
    this.internalDisabled ? this.form.disable() : this.form.enable();
    this.stateChanges.next();
  }

  public get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  public constructor(
    formBuilder: FormBuilder,
    @Optional() @Self() public ngControl: NgControl,
    private readonly focusMonitor: FocusMonitor,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.form = formBuilder.group({
      year: ['2019'],
      month: ['01'],
      date: ['01'],
    });
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this.setDateformats();
  }

  public onChange: (v: Moment) => void = () => {};
  public onTouched: () => void = () => {};

  public get empty(): boolean {
    const durationValue = this.form.value;
    return !durationValue.year && !durationValue.month && !durationValue.day;
  }
  public get errorState(): boolean {
    return this.ngControl?.invalid || (this.form.invalid && this.touched);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.dateFormat) {
      this.setDateformats();
    }
  }

  public onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.elementRef.nativeElement.querySelector('input').focus();
    }
  }

  public setDescribedByIds(ids: string[]): void {
    const controlElement = this.elementRef.nativeElement.querySelector('.nimbus-date-range-input-container')!;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  public writeValue(val: Moment | null): void {
    this.value = val;
    this.changeDetectorRef.markForCheck();
  }

  public registerOnChange(fn: (v: Moment) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onFocusIn(): void {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
      const defaultInput = this.datePartInputs.first;
      this.focusMonitor.focusVia(defaultInput, 'program');
      defaultInput.nativeElement.select();
    }
  }

  public onFocusOut(event: FocusEvent): void {
    if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  public autoFocusNext(control: AbstractControl, nextIndex: number, requiredLength?: number): void {
    const nextElement = this.datePartInputs.get(nextIndex)?.nativeElement;
    if (!control.errors && nextElement && control.value.length === requiredLength) {
      this.focusMonitor.focusVia(nextElement, 'program');
      nextElement.select();
    }
  }

  public autoFocusPrev(control: AbstractControl, prevIndex: number, noLimit: boolean = false): void {
    const prevElement = this.datePartInputs.get(prevIndex)?.nativeElement;
    if (control.value.length < 1 || noLimit) {
      this.focusMonitor.focusVia(prevElement, 'program');
      prevElement.select();
    }
  }

  public handleInput(control: AbstractControl, nextIndex?: number, requiredLength?: number): void {
    this.autoFocusNext(control, nextIndex, requiredLength);
    this.onChange(this.value);
  }

  public onBlur(control: AbstractControl, maxLength: number): void {
    if (control.value?.length > 0 && control.value?.length < maxLength) {
      control.setValue(control.value.padStart(maxLength, '0'));
    }
  }

  public onFocus(index: number) {
    this.datePartInputs.get(index).nativeElement.focus();
  }

  public ngOnDestroy(): void {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  private setDateformats(): void {
    switch (this.dateFormat) {
      case DateFormat.English:
        this.separator = '/';
        this.dateParts = [
          { type: 'month', maxLength: 2, maxValue: 12 },
          { type: 'date', maxLength: 2, maxValue: 31 },
          { type: 'year', maxLength: 4 },
        ];
        break;

      case DateFormat.UK:
      case DateFormat.Spanish:
      case DateFormat.French:
      case DateFormat.Italian:
        this.separator = '/';
        this.dateParts = [
          { type: 'date', maxLength: 2, maxValue: 31 },
          { type: 'month', maxLength: 2, maxValue: 12 },
          { type: 'year', maxLength: 4 },
        ];
        break;

      case DateFormat.Dutch:
        this.separator = '-';
        this.dateParts = [
          { type: 'date', maxLength: 2, maxValue: 31 },
          { type: 'month', maxLength: 2, maxValue: 12 },
          { type: 'year', maxLength: 4 },
        ];
        break;

      case DateFormat.German:
      case DateFormat.Danish:
      case DateFormat.Swedish:
        this.separator = '.';
        this.dateParts = [
          { type: 'date', maxLength: 2, maxValue: 31 },
          { type: 'month', maxLength: 2, maxValue: 12 },
          { type: 'year', maxLength: 4 },
        ];
        break;

      default:
        this.separator = '/';
        this.dateParts = [
          { type: 'month', maxLength: 2, maxValue: 12 },
          { type: 'date', maxLength: 2, maxValue: 31 },
          { type: 'year', maxLength: 4 },
        ];
    }
  }
}
